import React from 'react';

const getBalanceStatus = (bal, weeklyAmt) => {
    if (isNaN(bal)) return { label: '—', color: 'text-gray-400', barColor: 'bg-gray-300', pct: 0 };
    if (bal <= 0) return { label: 'Overpaid', color: 'text-green-600', barColor: 'bg-green-500', pct: 0 };
    const pct = weeklyAmt > 0 ? Math.min((bal / weeklyAmt) * 100, 150) : 100;
    if (bal <= 1000) return { label: 'Excellent', color: 'text-green-600', barColor: 'bg-green-500', pct };
    if (bal <= 3000) return { label: 'Low', color: 'text-blue-600', barColor: 'bg-blue-500', pct };
    if (bal <= 5000) return { label: 'High', color: 'text-orange-500', barColor: 'bg-orange-400', pct };
    return { label: 'Critical', color: 'text-red-600', barColor: 'bg-red-500', pct };
};

const FinancialInfo = ({ carDetails = {}, formatMoney = (v) => v }) => {
    // Local formatter: comma separators + 2 decimal places
    const formatMoneyDisplay = (value) => {
        if (value === null || value === undefined || value === '-' || value === '') return '-';
        const num = Number(value);
        if (isNaN(num)) return '-';
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
    };

    const display = (val) => {
        try {
            const formatted = formatMoney && typeof formatMoney === 'function' ? formatMoney(val) : null;
            if (formatted || formatted === 0) return formatted;
        } catch (e) {
            // ignore and fallback
        }
        return formatMoneyDisplay(val);
    };

    const actualBal = parseFloat(carDetails.actual_bal);
    const weekly = parseFloat(carDetails.weekly_amount) || 0;
    const status = getBalanceStatus(actualBal, weekly);
    const barWidth = Math.min(status.pct, 100);

    return (
        <div className="bg-gray-50 rounded-sm border border-gray-200 p-3">
            <h3 className="text-xs font-bold text-[#2a2e30] mb-2.5 flex items-center">
                <div className="w-2 h-2 bg-[#FF5733] rounded-full mr-2"></div>
                Financial
            </h3>

            {/* Hero: Actual Balance */}
            <div className="text-center mb-2">
                <div className={`text-sm font-semibold font-mono leading-tight ${status.color}`}>
                    {display(carDetails.actual_bal)}
                </div>
                <div className="text-xs text-gray-500">Actual Balance</div>
            </div>

            {/* Progress bar */}
            {weekly > 0 && (
                <div className="mb-3">
                    <div className="flex justify-between text-xs mb-1">
                        <span className={`font-semibold ${status.color}`}>{status.label}</span>
                        <span className="text-gray-500">{Math.round(status.pct)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                            className={`h-1.5 rounded-full transition-all duration-300 ${status.barColor}`}
                            style={{ width: `${barWidth}%` }}
                        />
                    </div>
                </div>
            )}

            <div className="space-y-2 text-xs border-t border-gray-200 pt-2">
                <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center">
                    <span className="text-gray-700 font-medium">Balance B/F:</span>
                    <span className={`font-mono font-medium text-xs xl:text-right ${parseFloat(carDetails.bal_bf) < 0 ? 'text-green-600' : 'text-[#2a2e30]'}`}>{display(carDetails.bal_bf)}</span>
                </div>
                <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center">
                    <span className="text-gray-700 font-medium">Weekly Bal:</span>
                    <span className="font-mono font-medium text-[#2a2e30] text-xs xl:text-right">{display(carDetails.bal)}</span>
                </div>
                <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center">
                    <span className="text-gray-700 font-medium">Weekly Amt:</span>
                    <span className="font-mono font-medium text-[#2a2e30] text-xs xl:text-right">{display(carDetails.weekly_amount)}</span>
                </div>
            </div>
        </div>
    );
};

export default FinancialInfo;
